import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Mail, ArrowLeft } from "lucide-react";
import { Link } from "wouter";

export default function ForgotPassword() {
  const [identifier, setIdentifier] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!identifier) {
      toast({
        title: "Email or username required",
        description: "Enter the email address or the username on your account",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      await apiRequest("POST", "/api/auth/forgot-password", { identifier });
      
      setEmailSent(true);
      toast({
        title: "Email sent",
        description: "If an account exists, a link is on its way to the address on it."
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send reset email. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Mail className="h-6 w-6 text-primary" />
            </div>
            <CardTitle>Check Your Email</CardTitle>
            <CardDescription>
              {/* The link goes to the address stored on the account, which is not
                  necessarily what was typed here, a username has no inbox. */}
              If we found an account, a link is on its way to the email address on it.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center text-sm text-muted-foreground">
              <p>Click the link in the email to reset your password.</p>
              <p className="mt-2">The link will expire in 1 hour.</p>
            </div>
            <Button
              className="w-full"
              variant="outline"
              onClick={() => setEmailSent(false)}
              data-testid="button-resend"
            >
              Didn't receive the email? Try again
            </Button>
            <Link href="/login">
              <Button variant="ghost" className="w-full" data-testid="link-back-login">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Forgot Password?</CardTitle>
          <CardDescription>
            Enter your email address or your username and we'll send a reset link to the
            email on your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="identifier">Email or username</Label>
              <Input
                id="identifier"
                type="text"
                placeholder="your.email@example.com or your username"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                disabled={isLoading}
                required
                data-testid="input-email"
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
              data-testid="button-submit"
            >
              {isLoading ? "Sending..." : "Send Reset Link"}
            </Button>

            <Link href="/find-account">
              <Button variant="ghost" className="w-full" data-testid="link-find-account">
                I don't know which email I used
              </Button>
            </Link>

            <Link href="/login">
              <Button variant="ghost" className="w-full" data-testid="link-back-login">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Login
              </Button>
            </Link>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
